"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import { useIsDesktop } from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import {
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"
import { HistoryData } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
  margin: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 88px;
    margin: 88px 0;
    margin-top: 168px;
  }
`

const StyledContainer = styled.div<{ $inView: boolean; $isReversed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  position: relative;
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  flex-direction: column-reverse;
  gap: 24px;
  padding-inline-start: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-inline-start: 0;
    opacity: ${(props) => (props.$inView ? 1 : 0)};
    display: grid;
    grid-template-columns: 1fr 1fr;
    img {
      order: ${(props) => (props.$isReversed ? 1 : 0)};
      justify-self: ${(props) =>
        props.$isReversed ? "flex-start" : "flex-end"};
    }
    gap: 120px;
    flex-direction: ${(props) => (props.$isReversed ? "row-reverse" : "row")};
  }
`

const TextBox = styled.div<{ $isReversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;

  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 50%;
    text-align: ${(p) => (p.$isReversed ? "right" : "left")};
    justify-self: ${(props) => (props.$isReversed ? "flex-end" : "flex-start")};
  }
`

const StyledImage = styled(Image)`
  object-fit: cover;
  border-radius: 16px;
  height: 184px;
  width: 304px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 487px;
    height: 260px;
    transform: translateY(-80px);
  }
`

const TimelineLine = styled.div`
  position: absolute !important;
  width: 2px;
  height: 100%;
  left: 0;
  top: 0;
  background-image: url("/images/historyPageLine.png");
  background-repeat: repeat-y;
  background-size: 2px 720px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    background-image: url("/images/historyMobileLine.png");
    left: 50%;
    transform: translateX(-50%);
  }
`

const StyledPin = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 200px;
  z-index: 1;
  box-sizing: content-box;
  border: 3px solid var(--White, #fff);
  background: var(--Brand-Light, #1bbd77);
  box-shadow: 0px 0px 16px 0px rgba(27, 189, 119, 0.8);
  position: absolute;
  left: -7px;
  top: 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    left: 50%;
    transform: translateX(-50%); // Center the pin horizontally
  }
`

const StyledTypography = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 24px !important;
  line-height: 24px !important;
  font-weight: 700 !important;
  margin: 0px;
  color: ${sharedColors.brandMain};
`

const MobileBusWrapper = styled.div<{ inView: boolean }>`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition:
    top 8s ease,
    opacity 0.1s ease 6s;
  top: ${({ inView }) => (inView ? "100%" : "0")};
  opacity: ${({ inView }) => (inView ? 0 : 1)};
  z-index: 2;
  width: 32px;
  height: 32px;
`

type Props = {
  data: HistoryData[]
}

const HistoryTimeLine: React.FC<Props> = ({ data }) => {
  const [animatingItems, setAnimatingItems] = useState<boolean[]>([])
  const { inView, ref } = useInView({ ...useInViewConfig, threshold: 0 })
  useEffect(() => {
    if (inView) {
      setAnimatingItems(new Array(data.length).fill(false))
      data.forEach((_, index) => {
        const delay = (index - 0.5) * 1000 // Adjust timing to match bus movement
        setTimeout(() => {
          setAnimatingItems((prev) => {
            const newState = [...prev]
            newState[index] = true
            return newState
          })
        }, delay)
      })
    }
  }, [inView, data.length])
  const isDesktop = useIsDesktop()
  return (
    <Container paddingMobile={true}>
      <StyledLayout ref={ref}>
        <TimelineLine />
        <MobileBusWrapper inView={inView}>
          <Image
            src="/images/busHistoryMobile.png"
            alt="History Timeline Bus"
            width={32}
            height={120}
          />
        </MobileBusWrapper>
        {data.map((el, index) => (
          <StyledContainer
            $inView={true} // remove the inView animation for now
            $isReversed={index % 2 === 0}
            key={index}
          >
            <StyledImage
              src={getUmbracoMediaUrl(
                isDesktop ? el.imageForDesktop : el.imageForMobil,
              )}
              alt={el.title}
              width={487}
              height={260}
            />
            <StyledPin />
            <TextBox $isReversed={index % 2 === 0}>
              <StyledTypography component="h2" color={sharedColors.brandDark}>
                {el.years}
              </StyledTypography>
              <Typography variant="h3">{el.title}</Typography>
              <Typography variant="body1">{el.subtitle}</Typography>
            </TextBox>
          </StyledContainer>
        ))}
      </StyledLayout>
    </Container>
  )
}

export default HistoryTimeLine
